import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchBlogs } from "../api/blogApi";
import { fetchBlogCategories } from "../api/blogCategoryApi";
import { toastError } from "../utils/toast";
import { withBase } from "../config/env";
import "./css/blogs.css";

const PAGE_SIZE = 4;

function ShimmerCard() {
  return (
    <article className="blog-card shimmer-card" aria-hidden="true">
      <div className="blog-cover shimmer-block" />
      <div className="blog-body">
        <div className="shimmer-line title" />
        <div className="shimmer-line meta" />
        <div className="shimmer-line" />
        <div className="shimmer-line short" />
        <div className="shimmer-line btn" />
      </div>
    </article>
  );
}

function ShimmerGrid({ count = PAGE_SIZE }) {
  return (
    <div className="blogs-grid" role="status" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <ShimmerCard key={i} />
      ))}
    </div>
  );
}

/** Sidebar categories shimmer (pills) */
function SidebarCatsShimmer() {
  return (
    <div className="cat-list" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="shimmer-pill" />
      ))}
    </div>
  );
}

/** Recent posts shimmer (list tiles) */
function RecentPostsShimmer() {
  return (
    <ul className="recent-posts-list" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="list-tile">
          <div className="list-tile-link shimmer-tile">
            <div className="list-tile-media shimmer-block" />
            <div className="list-tile-body">
              <div className="shimmer-line small" />
              <div className="shimmer-line tiny" />
              <div className="shimmer-line tiny short" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function BlogsBody() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);

  const [page, setPage] = useState(1);

  const location = useLocation();
  const navigate = useNavigate();

  // Parse query params
  const params = new URLSearchParams(location.search);
  const categoryFromQuery = params.get("category");       // id
  const categoryNameFromQuery = params.get("categoryName"); // name (optional)

  // load blogs
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchBlogs();
        if (on) setBlogs(Array.isArray(data) ? data : []);
      } catch {
        toastError("Failed to load blogs.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  // load categories of type "Blogs" only
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setCatLoading(true);
        const data = await fetchBlogCategories?.();
        const list = Array.isArray(data) ? data : data?.data || [];
        const blogsOnly = list.filter((c) => (c?.type || "").toLowerCase() === "blogs");
        if (on) setCategories(blogsOnly);
      } catch {
        toastError("Failed to load categories.");
        setCategories([]);
      } finally {
        setCatLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  // If URL contains category, set selected when categories load
  useEffect(() => {
    if (!categories.length) return;
    // try id first
    if (categoryFromQuery) {
      const found = categories.find((c) => String(c.id) === String(categoryFromQuery));
      if (found) {
        setSelectedCat({ id: found.id, title: found.title });
        return;
      }
    }
    // fallback by name
    if (categoryNameFromQuery) {
      const foundByName = categories.find((c) => c.title === categoryNameFromQuery);
      if (foundByName) {
        setSelectedCat({ id: foundByName.id, title: foundByName.title });
      }
    }
  }, [categories, categoryFromQuery, categoryNameFromQuery]);

  // category -> count from blog list
  const catCounts = useMemo(() => {
    const m = new Map();
    for (const b of blogs) {
      const cid = b?.category_id;
      if (cid != null) m.set(cid, (m.get(cid) || 0) + 1);
    }
    return m;
  }, [blogs]);

  // filter blogs by selected category (if any)
  const filteredBlogs = useMemo(() => {
    if (!selectedCat) return blogs;
    const cid = selectedCat.id;
    return blogs.filter((b) =>
      b?.category_id != null
        ? Number(b.category_id) === Number(cid)
        : b?.category_name === selectedCat.title
    );
  }, [blogs, selectedCat]);

  // recent 3 blogs
  const recentBlogs = useMemo(() => {
    const getDate = (x) => new Date(x?.created_at || x?.createdAt || x?.date || 0).getTime();
    return [...blogs].sort((a, b) => getDate(b) - getDate(a)).slice(0, 3);
  }, [blogs]);

  // pagination from filtered list
  const totalPages = Math.max(1, Math.ceil(filteredBlogs.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredBlogs.slice(start, start + PAGE_SIZE);
  }, [filteredBlogs, page]);

  useEffect(() => { setPage(1); }, [selectedCat]);

  const go = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getExcerpt = (html, len = 80) => {
    if (!html) return "";
    const txt = String(html).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    return txt.length > len ? txt.slice(0, len) + "…" : txt;
  };

  const selectCategory = (c) => {
    setSelectedCat(c);
    // keep URL in sync for shareability
    if (c) {
      navigate(`/blogs?category=${c.id}&categoryName=${encodeURIComponent(c.title)}`);
    } else {
      navigate(`/blogs`);
    }
  };

  return (
    <main className="blogs-body">
      <section className="blogs-section">
        <h1 className="blogs-heading">Blogs</h1>
        <p className="blogs-subtext">Latest articles, guides, and updates.</p>

        <div className="blogs-layout">
          {/* SIDEBAR */}
          <aside className="blogs-sidebar" aria-label="Blog Sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-title">Categories</h3>

              {catLoading ? (
                <SidebarCatsShimmer />
              ) : (
                <>
                  <button
                    className={`cat-item ${!selectedCat ? "active" : ""}`}
                    onClick={() => selectCategory(null)}
                    disabled={catLoading}
                  >
                    <span>All</span>
                    <span className="cat-count">{blogs.length}</span>
                  </button>

                  <div className="cat-list">
                    {!catLoading && categories.length === 0 && (
                      <div className="cat-empty">No categories</div>
                    )}
                    {!catLoading &&
                      categories.map((c) => (
                        <button
                          key={c.id ?? c.title}
                          className={`cat-item ${selectedCat?.id === c.id ? "active" : ""}`}
                          onClick={() => selectCategory({ id: c.id, title: c.title })}
                        >
                          <span>{c.title}</span>
                          <span className="cat-count">{catCounts.get(c.id) ?? 0}</span>
                        </button>
                      ))}
                  </div>
                </>
              )}
            </div>

            {/* RECENT POSTS */}
            <div className="sidebar-card">
              <h3 className="sidebar-title small">Recent Posts</h3>
              {loading ? (
                <RecentPostsShimmer />
              ) : (
                <ul className="recent-posts-list">
                  {recentBlogs.map((b) => {
                    const id = b?.id ?? b?._id ?? b?.slug;
                    const title = b?.title || "Untitled";
                    const cover = withBase(b?.thumbnail);
                    const createdAt = b?.created_at || b?.createdAt || b?.date;
                    const subtitle = getExcerpt(b?.excerpt || b?.content, 80);

                    return (
                      <li key={`recent-${id}`} className="list-tile">
                        <Link to={`/blogs/${id}`} state={{ blog: b }} className="list-tile-link">
                          <div className="list-tile-media">
                            {cover ? <img src={cover} alt={title} /> : <div className="list-tile-placeholder" />}
                          </div>
                          <div className="list-tile-body">
                            <h4 className="list-tile-title">{title}</h4>
                            {subtitle && <p className="list-tile-subtitle">{subtitle}</p>}
                            {createdAt && (
                              <time className="list-tile-date">
                                {new Date(createdAt).toLocaleDateString()}
                              </time>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                  {recentBlogs.length === 0 && !loading && <li className="recent-empty">No recent posts</li>}
                </ul>
              )}
            </div>
          </aside>

          {/* MAIN */}
          <div className="blogs-main">
            {loading ? (
              <ShimmerGrid />
            ) : paged.length === 0 ? (
              <div className="blogs-empty">No blogs found.</div>
            ) : (
              <>
                <div className="blogs-grid">
                  {paged.map((b) => {
                    const id = b?.id ?? b?._id ?? b?.slug;
                    const title = b?.title || "Untitled";
                    const cover = withBase(b?.thumbnail);
                    const createdAt = b?.created_at || b?.createdAt || b?.date;
                    const excerpt =
                      b?.excerpt ||
                      (b?.content
                        ? String(b.content).replace(/<[^>]+>/g, "").slice(0, 160) +
                          (String(b.content).length > 160 ? "…" : "")
                        : "");

                    return (
                      <article key={id} className="blog-card">
                        <Link to={`/blogs/${id}`} state={{ blog: b }} className="blog-cover-link">
                          {cover ? (
                            <img src={cover} alt={title} className="blog-cover" />
                          ) : (
                            <div className="blog-cover placeholder" />
                          )}
                        </Link>

                        <div className="blog-body">
                          <h3 className="blog-title">
                            <Link to={`/blogs/${id}`} state={{ blog: b }}>
                              {title}
                            </Link>
                          </h3>
                          <div className="blog-meta-row">
                            {b?.category_name && <span className="blog-chip">{b.category_name}</span>}
                            {createdAt && (
                              <time className="blog-date">{new Date(createdAt).toLocaleDateString()}</time>
                            )}
                          </div>
                          {excerpt && <p className="blog-excerpt">{excerpt}</p>}
                          <div className="blog-actions">
                            <Link to={`/blogs/${id}`} state={{ blog: b }} className="blog-readmore">
                              Read more →
                            </Link>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <nav className="pager" aria-label="Pagination">
                  <button onClick={() => go(page - 1)} disabled={page === 1}>
                    ‹ Prev
                  </button>
                  <ul className="pager-pages">
                    {Array.from({ length: totalPages }).map((_, i) => {
                      const p = i + 1;
                      return (
                        <li key={p}>
                          <button
                            className={`pager-btn ${p === page ? "active" : ""}`}
                            onClick={() => go(p)}
                            aria-current={p === page ? "page" : undefined}
                          >
                            {p}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <button onClick={() => go(page + 1)} disabled={page === totalPages}>
                    Next ›
                  </button>
                </nav>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
